import { useId, useState } from 'react'
import type { RenderRelationshipCard } from '../../../insightRenderShapes'
import { Divided, Note } from '../InsightsBlocks'
import { InsightCardHeader } from './InsightCardHeader'
import { RelationshipDetailsModal } from './RelationshipDetailsModal'
import { ProductImageDeck } from './ProductImageDeck'
import { InsightEntityNames } from './InsightEntityNames'
import { ProductPairRateBar } from './ProductPairRateBar'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../../shopifyUiTracking'
import type { RelationshipDensity } from './relationship-density'
import { shopifyComponentCopy } from '@testfeed/product-language'
/** Generic paired-entity view. Ranking, ratios, comparison copy and availability belong to the server. */
export function RelationshipsCard({
  presentation: card,
  density = 'current',
}: {
  presentation: RenderRelationshipCard
  density?: RelationshipDensity
}) {
  const shopify = useAppBridge()
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const modalId = `relationship-details-${id}`
  const rowsId = `relationship-rows-${id}`
  const [expanded, setExpanded] = useState(false)
  let message = null
  if (card.message) message = <Note>{card.message}</Note>
  let details = null
  if (density === 'compact' && card.detailsLabel)
    details = (
      <s-link
        commandFor={modalId}
        command="--show"
        onClick={() => trackShopifyAction(shopify, 'open-patterns', () => undefined)}
      >
        {card.detailsLabel}
      </s-link>
    )
  if (density === 'current' && card.rows.length > card.previewRows)
    details = (
      <s-button
        variant="tertiary"
        aria-expanded={expanded}
        aria-controls={rowsId}
        onClick={() => {
          if (expanded) trackShopifyAction(shopify, 'close-patterns', () => setExpanded(false))
          else trackShopifyAction(shopify, 'open-patterns', () => setExpanded(true))
        }}
      >
        {expanded
          ? shopifyComponentCopy.productPairsShowFewer
          : shopifyComponentCopy.productPairsShowAll.replace('{count}', String(card.rows.length))}
      </s-button>
    )
  const visibleRows =
    density === 'compact'
      ? card.rows.slice(1, card.previewRows)
      : expanded
        ? card.rows
        : card.rows.slice(0, card.previewRows)
  const items = visibleRows.map((row) => (
    <s-grid key={row.id} gridTemplateColumns="auto minmax(0, 1fr)" gap="small" alignItems="start">
      <ProductImageDeck left={row.left} right={row.right} />
      <div style={{ minInlineSize: 0, overflowWrap: 'anywhere' }}>
        <s-stack gap="small-100">
          {density === 'compact' ? (
            <s-text fontWeight="medium">
              <InsightEntityNames entities={[row.left, row.right]} separator=" and " />
            </s-text>
          ) : null}
          <Note>{row.summary}</Note>
          {density === 'current' ? (
            <ProductPairRateBar
              product={row.right.label}
              combinedWith={row.left.label}
              withoutPartnerPercent={row.rateComparison.withoutPartnerPercent}
              withPartnerPercent={row.rateComparison.withPartnerPercent}
            />
          ) : null}
        </s-stack>
      </div>
    </s-grid>
  ))
  const firstRow = card.rows[0]
  let compactLead = null
  if (density === 'compact' && firstRow)
    compactLead = (
      <s-grid gridTemplateColumns="auto minmax(0, 1fr)" gap="small" alignItems="start">
        <ProductImageDeck left={firstRow.left} right={firstRow.right} />
        <div style={{ minInlineSize: 0, overflowWrap: 'anywhere' }}>
          <s-stack gap="small-100">
            <Note>{shopifyComponentCopy.productPairsMostCommon}</Note>
            <s-text fontWeight="semibold">
              <InsightEntityNames entities={[firstRow.left, firstRow.right]} separator=" and " />
            </s-text>
            <Note>{firstRow.summary}</Note>
          </s-stack>
        </div>
      </s-grid>
    )
  return (
    <s-section heading={card.title}>
      <s-icon slot="graphic" type="cart-up" />
      <InsightCardHeader title={card.title} help={card.help} />
      <s-stack gap="base">
        {message}
        {compactLead}
        <div
          id={rowsId}
          role={density === 'current' && expanded ? 'region' : undefined}
          aria-label={density === 'current' && expanded ? 'All product pairs' : undefined}
          tabIndex={density === 'current' && expanded ? 0 : undefined}
        >
          <Divided items={items} />
        </div>
        {details}
        <s-divider />
        <Note>{card.footer}</Note>
      </s-stack>
      {density === 'compact' ? <RelationshipDetailsModal id={modalId} card={card} density={density} /> : null}
    </s-section>
  )
}
